import { z } from "zod";
import { AdvisorId, DateSchema } from "../Types";
export const ChatBotSessionIdSchema = z
    .string()
    .startsWith("BOT_")
    .regex(/^BOT_[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}$/)
    .brand("ChatBotSessionId");
export const ChatBotMessageIdSchema = z
    .string()
    .startsWith("BOTMSG_")
    .regex(/^BOTMSG_[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}$/)
    .brand("ChatBotMessageId");
// New pagination schema to match Proteus implementation
export const ChatBotPaginationMetadata = z.object({
    page: z.number(),
    pageSize: z.number(),
    hasNextPage: z.boolean(),
    nextPaginationToken: z.string().nullable(),
});
export const PageQueryStringSchema = z
    .string()
    .default("1")
    .transform((v) => Number(v))
    .refine((v) => Number.isInteger(v), { message: "Must be an integer" })
    .refine((v) => v >= 1, { message: "Must be greater than or equal to 1" })
    .describe("The page number, default is 1");
export const defaultPageSize = 10;
export const minPageSize = 1;
export const maxPageSize = 100;
export const PageSizeQueryStringSchema = z
    .string()
    .default(defaultPageSize.toString())
    .transform((v) => Number(v))
    .refine((v) => Number.isInteger(v), { message: "Must be an integer" })
    .refine((v) => v >= minPageSize, {
    message: `Must be greater than or equal to ${minPageSize}`,
})
    .refine((v) => v <= maxPageSize, {
    message: `Must be less than or equal to ${maxPageSize}`,
})
    .describe(`The number of items per page, default is ${defaultPageSize}. Must be between ${minPageSize} and ${maxPageSize}`);
export const ChatBotOrderBySchema = z
    .enum(["updatedAt asc", "updatedAt desc"])
    .default("updatedAt desc");
export const ChatBotSessionSchema = z.object({
    advisorId: AdvisorId,
    sessionId: ChatBotSessionIdSchema,
    title: z.string().optional(),
    createdAt: DateSchema,
    updatedAt: DateSchema,
    isDeleted: z.boolean().optional(),
});
export const ChatBotMessageSchema = z.object({
    sessionId: ChatBotSessionIdSchema,
    messageId: ChatBotMessageIdSchema,
    role: z.enum(["user", "assistant"]),
    status: z.enum(["pending", "sent", "received"]),
    content: z.string(),
    timestamp: DateSchema,
    isDeleted: z.boolean().optional(),
});
export const ChatBotSessionResponseBodySchema = z.object({
    data: z.array(ChatBotSessionSchema),
    pagination: ChatBotPaginationMetadata,
});
export const ChatBotMessageResponseBodySchema = z.object({
    data: z.array(ChatBotMessageSchema),
    pagination: ChatBotPaginationMetadata,
});
// Create Session
export const CreateChatBotSessionRequestSchema = z.object({
    title: z.string().optional(),
});
export const CreateChatBotSessionResponseSchema = z.object({
    data: ChatBotSessionSchema,
});
// Get Session
export const GetChatBotSessionByIdRequestParamsSchema = z.object({
    sessionId: ChatBotSessionIdSchema,
});
export const GetChatBotSessionByIdResponseSchema = z.object({
    data: ChatBotSessionSchema,
});
// Update Session
export const UpdateChatBotSessionRequestParamsSchema = z.object({
    sessionId: ChatBotSessionIdSchema,
});
export const UpdateChatBotSessionRequestBodySchema = z.object({
    title: z.string().optional(),
});
export const UpdateChatBotSessionResponseSchema = z.object({
    data: ChatBotSessionSchema,
});
// Delete Session
export const DeleteChatBotSessionRequestParamsSchema = z.object({
    sessionId: ChatBotSessionIdSchema,
});
export const DeleteChatBotSessionResponseSchema = z.object({
    success: z.boolean(),
    message: z.string().optional(),
});
// Get Sessions query parameters (missing schema that was referenced in Linear ticket)
export const GetChatBotSessionsQueryParamsSchema = z.object({
    page: PageQueryStringSchema.optional(),
    pageSize: PageSizeQueryStringSchema.optional(),
    orderBy: ChatBotOrderBySchema.optional(),
});
// Get Messages for Session
export const GetChatBotMessagesRequestParamsSchema = z.object({
    sessionId: ChatBotSessionIdSchema,
});
export const GetChatBotMessagesQueryParamsSchema = z.object({
    page: PageQueryStringSchema.optional(),
    pageSize: PageSizeQueryStringSchema.optional(),
    orderBy: z.enum(["timestamp asc", "timestamp desc"]).optional(),
});
// Send Message (already defined in Types.ts but added here for completeness)
export const SendChatBotMessageRequestSchema = z.object({
    sessionId: ChatBotSessionIdSchema,
    prompt: z.string(),
});
// This schema is used for the response from the BedRock API when sending a chat message
export const BedRockAPIChatBotMessageResponseSchema = z.object({
    text_response: z.string(),
});
export const SendChatBotMessageResponseSchema = z.object({
    data: BedRockAPIChatBotMessageResponseSchema,
});
//# sourceMappingURL=schemas.js.map